<template>
  <focus-trap :escapeDeactivates="false" :clickDeactivates="false">
    <div
      class="unlinked-measurement-results-popup"
      :tabindex="`-${layerNumber}`"
      @keyup.esc.stop="closeSelf"
    >
      <popup
        :buttons="['閉じる']"
        :content-style="{
          marginTop: '25px',
          padding: '0 30px'
        }"
        :layer-number="layerNumber"
        @click-close-mark="closeSelf"
        @close="closeSelf"
      >
        <template v-slot:content>
          <div class="content">
            <div class="title">未取込検査結果一覧</div>
            <div class="search">
              <search-area
                data-test="search-area"
                :period-flg="true"
                :period-label="'期間'"
                :placeholder="'検査名、検査機器'"
                :text-box-label="'検索単語'"
                :text-value="searchText"
                @clear-end-date="inputDate('searchEndDate', '')"
                @clear-start-date="inputDate('searchStartDate', '')"
                @clear="clearDate"
                @input-end-date="inputDate('searchEndDate', $event)"
                @input-start-date="inputDate('searchStartDate', $event)"
                @input="inputText"
              />
            </div>
            <div class="list">
              <measurement-result-list-table
                v-if="filteredMeasurementResults.length > 0"
                data-test="measurement-result-list-table"
                :measurement-results="filteredMeasurementResults"
                @click="openEditPopup"
              />
              <div v-else class="no-data" data-test="no-data">
                <span>※対象のデータはありません</span>
              </div>
            </div>
          </div>
        </template>
      </popup>
      <measurement-result-edit-popup
        v-if="showEditPopup"
        data-test="measurement-result-edit-popup"
        :can-delete="true"
        :is-patient-linked="false"
        :layer-number="layerNumber + 1"
        :measurement-result-id="selectedMeasurementResultId"
        :patient-id="patientId"
        :should-fetch-data="true"
        @close="closeEditPopup"
      />
    </div>
  </focus-trap>
</template>

<script>
import MeasurementResultListTable from '@/components/parts/organisms/MeasurementResultListTable'
import MeasurementResultEditPopup from '@/components/popups/MeasurementResultEditPopup'
import Popup from '@/components/popups/Popup'
import SearchArea from '@/components/parts/molecules/SearchArea'
import { FocusTrap } from 'focus-trap-vue'
import { compareByKeys } from '@/utils/compare'
import { mapGetters } from 'vuex'

export default {
  name: 'UnlinkedMeasurementResultsPopup',

  components: {
    FocusTrap,
    MeasurementResultListTable,
    MeasurementResultEditPopup,
    Popup,
    SearchArea
  },

  props: {
    layerNumber: { type: Number, default: 1 },
    patientId: { type: Number }
  },

  data() {
    return {
      searchEndDate: '',
      searchStartDate: '',
      searchText: '',
      selectedMeasurementResultId: 0,
      showEditPopup: false
    }
  },

  computed: {
    ...mapGetters({
      getCategory: 'master/getMasterLaboratoryCategoryIndexedByCode',
      getInstrument: 'master/getMasterLaboratoryInstrumentIndexedByCode',
      getMeasurementResults: 'measurementResults/getDataByPatientId',
      lookOnlyFlg: 'auth/lookOnlyFlg'
    }),
    filteredMeasurementResults() {
      return this.unlinkedMeasurementResults
        .filter(v => this.filterByDate(v) && this.filterByText(v))
        .sort((a, b) => compareByKeys(a, b, ['date', 'time', 'id']))
    },
    trimmedSearchText() {
      return this.searchText.toLowerCase().trim()
    },
    unlinkedMeasurementResults() {
      const unlinkedMeasurementResults = this.getMeasurementResults(0) || []
      return unlinkedMeasurementResults.map(v => {
        const categoryCode = v.masterLaboratoryCategoryCode
        const instrumentCode = v.masterLaboratoryInstrumentCode
        const category = this.getCategory(categoryCode)
        const instrument = this.getInstrument(instrumentCode)
        return { ...v, category, instrument }
      })
    }
  },

  methods: {
    clearDate() {
      this.searchStartDate = ''
      this.searchEndDate = ''
    },
    closeEditPopup() {
      this.showEditPopup = false
      this.selectedMeasurementResultId = 0
    },
    closeSelf() {
      this.$emit('close')
    },
    filterByDate(measurementResult) {
      const date = measurementResult.date
      const startDate = this.searchStartDate
      const endDate = this.searchEndDate
      if (startDate !== '' && endDate !== '') {
        return startDate <= date && date <= endDate
      } else if (startDate !== '') {
        return startDate <= date
      } else if (endDate !== '') {
        return date <= endDate
      } else {
        return true
      }
    },
    filterByText(measurementResult) {
      const { category, instrument } = measurementResult
      return (
        this.matchesSearchText(category.name) ||
        this.matchesSearchText(instrument.name)
      )
    },
    inputDate(key, date) {
      this[key] = date
    },
    inputText(text) {
      this.searchText = text
    },
    matchesSearchText(itemText) {
      return itemText.toLowerCase().includes(this.trimmedSearchText)
    },
    openEditPopup(measurementResult) {
      this.selectedMeasurementResultId = measurementResult.id
      this.showEditPopup = true
    }
  }
}
</script>

<style lang="scss" scoped>
.unlinked-measurement-results-popup {
  position: absolute;
  top: 0;
  left: 0;
  .content {
    box-sizing: border-box;
    > .title {
      font-size: 16px;
      font-weight: bold;
      text-align: left;
    }
    > .search {
      margin-top: 15px;
      min-width: 900px;
      padding-right: 25px;
    }
    > .list {
      margin-top: 25px;
      height: 481px;
      min-width: 900px;
      padding-right: 25px;
      ::v-deep .table-body {
        max-height: 441px;
      }
      > .no-data {
        font-size: 14px;
        font-weight: bold;
      }
    }
  }
}
</style>
